import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import {
  HiX,
  HiOutlineChatAlt2,
  HiOutlineThumbUp,
  HiOutlineThumbDown,
  HiCheck,
} from "react-icons/hi";

import { Modal } from "MVVM/Components/Wrappers/Existing/modal";
import { FEEDBACK_SUBMIT } from "MVVM/GraphQL/mutations/feedbackSubmit";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import {
  buildFeedbackMessage,
  TFeedbackSentiment,
} from "./buildFeedbackMessage";
import {
  FeedbackButton,
  ModalContainer,
  ModalHeader,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  SentimentRow,
  SentimentTile,
  Section,
  SectionLabel,
  ChipList,
  Chip,
  TextArea,
  CharCount,
  ModalFooter,
  ErrorText,
  PrimaryButton,
  SuccessState,
  SuccessBadge,
} from "./styledComponents";

const MAX_CHARS = 1000;
const COUNTER_FROM = 800;
const SUCCESS_CLOSE_MS = 1600;

type TTopicCopy = {
  title?: string;
  subtitle: string;
  slackTag: string;
  options?: Record<TFeedbackSentiment, string[]>;
};

const TOPIC_COPY: Record<"new-nav" | "match", TTopicCopy> = {
  "new-nav": {
    subtitle:
      "You're using our new navigation experience, so we'd love to hear how it's going. Anything else on your mind is welcome too.",
    slackTag: "New nav experience",
  },
  match: {
    title: "How's the match page?",
    subtitle: "A couple of taps helps shape what we build next.",
    slackTag: "Match page",
    options: {
      up: [
        "Scoreboard",
        "Match VOD",
        "Your Performance",
        "Duels",
        "Highlights",
        "Design",
        "Speed",
      ],
      down: [
        "Missing stats",
        "Wrong data",
        "Slow to load",
        "VOD issues",
        "Hard to navigate",
        "Highlights",
        "Something else",
      ],
    },
  },
};

const SENTIMENTS: {
  value: TFeedbackSentiment;
  label: string;
  prompt: string;
  Icon: typeof HiOutlineThumbUp;
}[] = [
  {
    value: "up",
    label: "Loving it",
    prompt: "What stood out?",
    Icon: HiOutlineThumbUp,
  },
  {
    value: "down",
    label: "Needs work",
    prompt: "What should we fix?",
    Icon: HiOutlineThumbDown,
  },
];

/**
 * Uncontrolled by default, so the site header keeps its own trigger button.
 * The site nav drives it from the account panel instead: that panel unmounts
 * when it closes, so the modal has to live above it.
 */
const FeedbackModal = ({
  open,
  onOpenChange,
  hideTrigger,
  topic,
  context,
  initialSentiment,
  onSent,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  topic?: keyof typeof TOPIC_COPY;
  context?: string;
  initialSentiment?: TFeedbackSentiment;
  onSent?: () => void;
} = {}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const setIsOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  const [message, setMessage] = useState("");
  const [sentiment, setSentiment] = useState<TFeedbackSentiment>();
  const [picked, setPicked] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "error" | "sent">("idle");
  const { pathname } = useLocation();
  const { track } = useAnalyticsContext();
  const copy = topic ? TOPIC_COPY[topic] : null;
  const options = copy?.options;

  const [submitFeedback, { loading }] = useMutation(FEEDBACK_SUBMIT);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setMessage("");
    setSentiment(undefined);
    setPicked([]);
    setStatus("idle");
  }, [setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) setSentiment(initialSentiment);
  }, [isOpen, initialSentiment]);

  useEffect(() => {
    if (status !== "sent") return;
    const timeout = setTimeout(handleClose, SUCCESS_CLOSE_MS);
    return () => clearTimeout(timeout);
  }, [status, handleClose]);

  const chooseSentiment = (next: TFeedbackSentiment) => {
    if (next === sentiment) return;
    setSentiment(next);
    setPicked([]);
  };

  const toggleOption = (option: string) =>
    setPicked((prev) =>
      prev.includes(option)
        ? prev.filter((entry) => entry !== option)
        : [...prev, option],
    );

  const canSubmit = options ? !!sentiment : !!message.trim();

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setStatus("idle");

    try {
      const { data } = await submitFeedback({
        variables: {
          input: {
            message: buildFeedbackMessage({
              tag: copy?.slackTag,
              sentiment,
              options: picked,
              context,
              note: message,
            }),
            pageUrl: pathname,
          },
        },
      });
      if (data?.feedbackSubmit?.success === false) throw new Error();
    } catch {
      setStatus("error");
      return;
    }

    track("Feedback Submitted", {
      topic: topic ?? "general",
      sentiment,
      options: picked,
    });
    setStatus("sent");
    onSent?.();
  }, [
    canSubmit,
    submitFeedback,
    copy,
    sentiment,
    picked,
    context,
    message,
    pathname,
    track,
    topic,
    onSent,
  ]);

  const activeSentiment = SENTIMENTS.find((entry) => entry.value === sentiment);
  // Topic mode keeps the note behind the sentiment pick; general mode is the note.
  const showNote = !options || !!sentiment;

  const dialog = (
    <Modal
      isModalOpen={isOpen}
      setModalOpen={(next: boolean) => !next && handleClose()}
      handleModalClose={undefined}
      disableOutsideClose={false}
    >
      <ModalContainer
        role="dialog"
        aria-modal="true"
        aria-labelledby="feedback-modal-title"
      >
        <ModalHeader>
          <div>
            <HeaderTitle id="feedback-modal-title">
              {copy?.title ?? "Submit Feedback"}
            </HeaderTitle>
            {status !== "sent" && (
              <HeaderSubtitle>
                {copy?.subtitle ??
                  "Share your thoughts, suggestions, or anything that went wrong. It goes straight to the team."}
              </HeaderSubtitle>
            )}
          </div>
          <CloseButton type="button" aria-label="Close" onClick={handleClose}>
            <HiX size={20} />
          </CloseButton>
        </ModalHeader>

        {status === "sent" ? (
          <SuccessState role="status" onClick={handleClose}>
            <SuccessBadge>
              <HiCheck size={24} />
            </SuccessBadge>
            Thanks, that goes straight to the team.
          </SuccessState>
        ) : (
          <>
            {options && (
              <SentimentRow>
                {SENTIMENTS.map(({ value, label, Icon }, index) => (
                  <SentimentTile
                    key={value}
                    type="button"
                    $selected={sentiment === value}
                    aria-pressed={sentiment === value}
                    onClick={() => chooseSentiment(value)}
                    autoFocus={index === 0}
                  >
                    <Icon size={24} aria-hidden />
                    {label}
                  </SentimentTile>
                ))}
              </SentimentRow>
            )}

            {options && activeSentiment && (
              <Section key={activeSentiment.value}>
                <SectionLabel>{activeSentiment.prompt}</SectionLabel>
                <ChipList>
                  {options[activeSentiment.value].map((option) => (
                    <Chip
                      key={option}
                      type="button"
                      $selected={picked.includes(option)}
                      aria-pressed={picked.includes(option)}
                      onClick={() => toggleOption(option)}
                    >
                      {option}
                    </Chip>
                  ))}
                </ChipList>
              </Section>
            )}

            {showNote && (
              <Section>
                <TextArea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={
                    options
                      ? "Anything else? (optional)"
                      : "Tell us what you think..."
                  }
                  aria-label="Feedback"
                  maxLength={MAX_CHARS}
                  autoFocus={!options}
                />
                {message.length >= COUNTER_FROM && (
                  <CharCount $isOver={message.length >= MAX_CHARS}>
                    {message.length}/{MAX_CHARS}
                  </CharCount>
                )}
              </Section>
            )}

            <ModalFooter>
              {status === "error" && (
                <ErrorText role="alert">
                  Couldn't send that. Try again.
                </ErrorText>
              )}
              <PrimaryButton
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || loading}
              >
                {loading ? "Sending…" : "Send feedback"}
              </PrimaryButton>
            </ModalFooter>
          </>
        )}
      </ModalContainer>
    </Modal>
  );

  return (
    <>
      {!hideTrigger && (
        <FeedbackButton onClick={() => setIsOpen(true)}>
          <HiOutlineChatAlt2 size={14} />
          Leave Feedback
        </FeedbackButton>
      )}

      {/* Portaled: a fixed overlay inside a transformed or clipped parent (the
          match hero, the nav panel) is positioned against that parent. */}
      {isOpen && createPortal(dialog, document.getElementById("root")!)}
    </>
  );
};

export { FeedbackModal };
